/**
 * Build a chronological activity log for a product family from nested timestamps.
 */
export function buildFamilyActivityLogs(family, options = {}) {
    if (!family) return [];

    const idPrefix = options.idPrefix ? `${options.idPrefix}-` : '';
    const familyName = family.name;
    const logs = [];
    const rulesets = family.ruleSets || [];
    const features = family.features || [];

    logs.push({
        id: `${idPrefix}fam-create`,
        activity: 'Family Initialized',
        item: familyName,
        familyName,
        familyId: family.id,
        targetId: family.id,
        date: family.createdAt || new Date().toISOString(),
        user: family.updatedBy || 'System',
        icon: 'fa-solid fa-box',
        color: 'var(--accent)'
    });

    if (family.updatedAt && family.createdAt) {
        const createdTime = new Date(family.createdAt).getTime();
        const updatedTime = new Date(family.updatedAt).getTime();
        if (updatedTime > createdTime + 1000) {
            const isReverted = (family.updatedBy || '').endsWith('(Reverted)');
            const cleanUser = isReverted ? family.updatedBy.replace(' (Reverted)', '') : family.updatedBy;
            logs.push({
                id: `${idPrefix}fam-update`,
                activity: isReverted ? 'Family Reverted' : 'Family Renamed',
                item: familyName,
                familyName,
                familyId: family.id,
                targetId: family.id,
                date: family.updatedAt,
                user: cleanUser || 'System',
                icon: isReverted ? 'fa-solid fa-clock-rotate-left' : 'fa-solid fa-pen',
                color: isReverted ? '#EF4444' : 'var(--accent)'
            });
        }
    }

    rulesets.forEach(rs => {
        logs.push({
            id: `${idPrefix}rs-map-${rs.id}`,
            activity: 'Ruleset Created',
            item: rs.name,
            familyName,
            familyId: family.id,
            targetId: rs.id,
            date: rs.createdAt || new Date().toISOString(),
            user: rs.updatedBy || 'System',
            icon: 'fa-solid fa-link',
            color: '#10B981'
        });

        if (rs.updatedAt && rs.createdAt) {
            const createdTime = new Date(rs.createdAt).getTime();
            const updatedTime = new Date(rs.updatedAt).getTime();
            if (updatedTime > createdTime + 1000) {
                const isReverted = (rs.updatedBy || '').endsWith('(Reverted)');
                const cleanUser = isReverted ? rs.updatedBy.replace(' (Reverted)', '') : rs.updatedBy;
                logs.push({
                    id: `${idPrefix}rs-update-${rs.id}`,
                    activity: isReverted ? 'Ruleset Reverted' : 'Ruleset Saved',
                    item: rs.name,
                    familyName,
                    familyId: family.id,
                    targetId: rs.id,
                    date: rs.updatedAt,
                    user: cleanUser || 'System',
                    icon: isReverted ? 'fa-solid fa-clock-rotate-left' : 'fa-solid fa-floppy-disk',
                    color: isReverted ? '#EF4444' : '#10B981'
                });
            }
        }
    });

    features.forEach(feat => {
        logs.push({
            id: `${idPrefix}feat-map-${feat.id}`,
            activity: 'Feature Created',
            item: feat.name,
            familyName,
            familyId: family.id,
            targetId: feat.id,
            date: feat.createdAt || new Date().toISOString(),
            user: feat.updatedBy || 'System',
            icon: 'fa-solid fa-puzzle-piece',
            color: 'var(--accent-orange)'
        });

        if (feat.updatedAt && feat.createdAt) {
            const createdTime = new Date(feat.createdAt).getTime();
            const updatedTime = new Date(feat.updatedAt).getTime();
            if (updatedTime > createdTime + 1000) {
                const isReverted = (feat.updatedBy || '').endsWith('(Reverted)');
                const cleanUser = isReverted ? feat.updatedBy.replace(' (Reverted)', '') : feat.updatedBy;
                logs.push({
                    id: `${idPrefix}feat-update-${feat.id}`,
                    activity: isReverted ? 'Feature Reverted' : 'Feature Saved',
                    item: feat.name,
                    familyName,
                    familyId: family.id,
                    targetId: feat.id,
                    date: feat.updatedAt,
                    user: cleanUser || 'System',
                    icon: isReverted ? 'fa-solid fa-clock-rotate-left' : 'fa-solid fa-floppy-disk',
                    color: isReverted ? '#EF4444' : 'var(--accent-orange)'
                });
            }
        }
    });

    return logs;
}

export function buildProjectActivityLogs(families) {
    if (!Array.isArray(families)) return [];
    return families.flatMap(family => buildFamilyActivityLogs(family, { idPrefix: family.id }));
}
